import { useState } from 'react';

import { Button, IconButton, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AddRounded as AddIcon, DeleteRounded as DeleteIcon } from '@material-ui/icons';

import { DeliveryPointClosure } from '../../types/delivery-point-details.type';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',

    '& .MuiButton-root': {
      textTransform: 'none',
    },

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  addButton: {
    alignSelf: 'flex-start',
  },
}));

const toDateInputValue = (isoString: string) => isoString.slice(0, 10);

const END_BEFORE_START_ERROR = 'La date de fin doit être postérieure à la date de début';

const buildDefaultClosure = (): DeliveryPointClosure => {
  const today = new Date().toISOString();
  return { from: today, to: today };
};

type Props = {
  closures: DeliveryPointClosure[];
  onChange: (closures: DeliveryPointClosure[]) => void;
};

export default function DeliveryPointClosureEditor({ closures, onChange }: Props) {
  const classes = useStyles();
  const [invalidRowIndexes, setInvalidRowIndexes] = useState<Record<number, boolean>>({});

  const handleFieldChange = (changedIndex: number, field: 'from' | 'to', rawValue: string) => {
    if (!rawValue) return;

    const changedClosure = { ...closures[changedIndex], [field]: new Date(rawValue).toISOString() };

    if (new Date(changedClosure.to) < new Date(changedClosure.from)) {
      setInvalidRowIndexes((previous) => ({ ...previous, [changedIndex]: true }));
      return;
    }

    setInvalidRowIndexes((previous) => {
      const next = { ...previous };
      delete next[changedIndex];
      return next;
    });

    onChange(closures.map((closure, index) => (index === changedIndex ? changedClosure : closure)));
  };

  const handleDelete = (removedIndex: number) => {
    setInvalidRowIndexes({});
    onChange(closures.filter((_closure, index) => index !== removedIndex));
  };

  // L'ajout se fait en fin de liste, donc les index en erreur restent valides : les effacer
  // masquerait le message d'une autre ligne encore fautive.
  const handleAdd = () => {
    onChange([...closures, buildDefaultClosure()]);
  };

  return (
    <div className={classes.root}>
      {closures.map((closure, index) => (
        <div className={classes.row} key={index}>
          <TextField
            id={`closure-${index}-from`}
            label="Début"
            type="date"
            size="small"
            variant="outlined"
              value={toDateInputValue(closure.from)}
            onChange={(event) => handleFieldChange(index, 'from', event.target.value)}
          />
          <TextField
            id={`closure-${index}-to`}
            label="Fin"
            type="date"
            size="small"
            variant="outlined"
              error={Boolean(invalidRowIndexes[index])}
            helperText={invalidRowIndexes[index] ? END_BEFORE_START_ERROR : undefined}
            value={toDateInputValue(closure.to)}
            onChange={(event) => handleFieldChange(index, 'to', event.target.value)}
          />
          <IconButton size="small" title="Supprimer la ligne" onClick={() => handleDelete(index)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ))}
      <Button size="small" variant="outlined" className={classes.addButton} startIcon={<AddIcon />} onClick={handleAdd}>
        Ajouter une fermeture
      </Button>
    </div>
  );
}
